import { type JSX } from 'react';

import { type paths } from '@depop/types-presentation-default-snowdon-api';

import { type CurrencyCode } from '../location/constants';

import { type ShippingSettingsProvider } from './aliases';

export enum ShippingId {
  MANUAL = 'MANUAL',
  USPS = 'USPS',
  Evri = 'MY_HERMES',
  Sendle = 'SENDLE',
  AusPost = 'AUS_POST',
}

export type NonManual = Exclude<ShippingId, ShippingId.MANUAL>;

export const NonManualShippingDisplayName = {
  [ShippingId.USPS]: 'USPS',
  [ShippingId.Evri]: 'Evri',
  [ShippingId.Sendle]: 'Sendle',
  [ShippingId.AusPost]: 'Australia Post',
};

export interface ShippingMethod {
  shippingProviderId: NonManual;
  shipFromAddressId: number;
  parcelSizeId: string;
  payer: string;
}

export type MarkAsShippedRequest = {
  parcelId: string;
  carrierName?: string;
  trackingNumber?: string;
};

export type ShippingProvidersResponse = {
  all_providers: string[];
  featured_providers: string[];
};

export type ParcelCost = {
  amount: string;
  currency: CurrencyCode;
  originalAmount?: string;
  originalCurrency?: CurrencyCode;
};

export type ParcelSize = {
  id: string;
  cost: ParcelCost;
  title: string;
  subtitle: string;
  maxWeight: string;
};

export type ShippingProviderParcelSizeResponse = {
  id: ShippingId;
  parcelSizes: ParcelSize[];
};

export type EstimatedShippingProviderParcelSizeResponse =
  paths['/presentation/api/v1/shipping/providers/depop/']['get']['responses']['200']['content']['application/json'];

export type EstimatedShippingProviderParcelSizeQuery =
  paths['/presentation/api/v1/shipping/providers/depop/']['get']['parameters']['query'];

export type EstimatedShippingProviderParcelSize = NonNullable<
  EstimatedShippingProviderParcelSizeResponse[number]['parcel_sizes']
>[number];

export type EstimatedShippingProviderParcelSizeCost = NonNullable<
  EstimatedShippingProviderParcelSizeResponse[number]['parcel_sizes']
>[number]['cost'];

export enum ShippingStatus {
  Shippable = 'SHIPPABLE',
  NotShippable = 'NOT_SHIPPABLE',
}

export type ShippingPreferences = Pick<
  ShippingSettingsProvider,
  'active' | 'id'
>;

export type UpdateShippingPreferencesRequest = {
  shipping_providers: ShippingPreferences[];
};

export type FormattedEstimatedParcelSizes = [
  {
    label: string;
    value: 'suggested';
    leftIcon?: JSX.Element;
    options: FormattedParcelSize[];
  },
  {
    label: string;
    value: 'change-size';
    options: FormattedParcelSize[];
  },
];

export type FormattedParcelSize = {
  label: string;
  value: string;
  content?: string;
  extra?: {
    cost: {
      amount?: string;
    };
    hideLabelFromPath: boolean;
  };
};

export enum UserType {
  Buyer = 'BUYER',
  Seller = 'SELLER',
}
